// ---------------------------------------------------------------------------
// Tariff EFFECTIVITY — is this version actually in force today, and for how
// much longer?
//
// WHY THIS EXISTS
// Every Drakenstein tariff expired 2026-06-30 with no successor: 21 tariffs,
// 98 meters that cannot be billed for any later period. Nobody noticed until a
// bill run came back short — because every one of those expired versions still
// carries `is_active = 1`, and the UI read that flag and showed them as
// healthy. ACTIVE and EFFECTIVE are different properties, and only one of them
// was modelled. This module models the other one.
//
// It is PRESENTATION. Nothing here writes, and `is_active` keeps exactly the
// meaning it has always had — effectivity is derived from `effective_from` /
// `effective_to`, which the rows already carry.
//
// ─────────────────────────────────────────────────────────────────────────
// THE AUTHORITATIVE RULE LIVES IN workers/shared/billing-core/coverage.ts
// ─────────────────────────────────────────────────────────────────────────
// That file is the single source of truth for what "covers" means, shared by
// bill-calculator and billing-api. This SPA is browser code and cannot import
// Worker source, so the rule is MIRRORED here — character-for-character where
// coverage.ts has a function for it, not re-derived by hand:
//
//  1. `toDateKey` below is character-for-character coverage.ts:168.
//  2. `nextDayKey` below is character-for-character coverage.ts:185.
//
// `effective_to` is the INCLUSIVE LAST DAY a version applies to — verified
// against live D1 (design spec §2, 2026-08-05): 10 consecutive version pairs
// abut with a ONE-day gap (`2026-03-31` → `2026-04-01`), ZERO with a
// zero-day gap, and 42 of 42 bounded versions end on a calendar month's last
// day. A version therefore covers `[effective_from, nextDayKey(effective_to))`
// — the SAME half-open conversion `resolveVersionCoverage` performs
// (coverage.ts:249-252) before comparing anything:
//
//     in force on D  ⟺  is_active
//                    AND effective_from <= D
//                    AND (effective_to IS NULL OR D < nextDayKey(effective_to))
//
// So a version ending 2026-06-30 is IN FORCE on 2026-06-30, and expires
// starting 2026-07-01 — the day AFTER its end, not the day itself.
//
// THIS FILE PREVIOUSLY GOT THIS BACKWARDS. An earlier version of this exact
// comment claimed `effective_to` was an EXCLUSIVE bound and cited a function,
// `versionCoversStart`, that has never existed in coverage.ts — it was
// written against a divergent branch's rewrite of that file and the claim
// was never checked against the version that actually shipped. The result: a
// version was badged "Expired" on its own last valid day, and every
// "Expires in N days" countdown was short by one. The product owner
// confirmed the intended reading independently and in the same terms
// `nextDayKey` computes: a version "ends when the next one starts, 1 April
// 00:00" — the same instant as "through 31 March", not the instant before
// it. The durable fix is to COMPUTE that instant with `nextDayKey`, exactly
// as coverage.ts does, rather than assert an inclusive/exclusive reading via
// a raw comparison and hope it is right — which is what let this propagate.
//
// NO ARBITRARY DATE ARITHMETIC DECIDES A STATE BOUNDARY. `daysBetween` and
// `nextDayKey` are the only two primitives involved here, both fixed, both
// mirrored from coverage.ts, and `daysBetween` only ever refines "in force"
// into "in force, but expiring soon" — an arithmetic bug there can change how
// much WARNING an operator gets, and can never change whether something
// reads as expired. The expired/in-force line is `nextDayKey` plus a string
// comparison, nothing invented locally.
// ---------------------------------------------------------------------------

/**
 * How far ahead an expiry is worth warning about, in days.
 *
 * 60, and the number is argued rather than picked:
 *
 *  - These are ANNUAL tariffs rolled at the South African municipal financial
 *    year boundary — they run 1 July to 30 June, so this warning fires once a
 *    year per tariff. The cost of being generous is one badge for two months
 *    out of twelve; the cost of being stingy is what already happened.
 *
 *  - It has to cover the whole capture window, not just its end. A municipal
 *    budget must be approved by 31 May (MFMA s24), tariffs are gazetted
 *    through June, and they take effect 1 July. A 30-day threshold first fires
 *    on 31 May — the same week the rates are finalised — leaving no slack for
 *    a late gazette and none at all if the operator is away for part of June.
 *    60 days fires ~1 May, while the successor is still in draft but the
 *    calendar is already known.
 *
 *  - 60 > 31 guarantees the warning is on screen for at least one full monthly
 *    bill-run cycle before it bites, so the operator meets it while doing the
 *    work it affects, not afterwards.
 */
export const EXPIRING_SOON_DAYS = 60;

/**
 * The only fields effectivity reads.
 *
 * Structurally typed and permissive for the same reason as
 * `TariffVersionWindow` in coverage.ts: every caller's richer row — the SPA's
 * `TariffVersion`, a D1 row — is accepted without a cast. `is_active` takes
 * D1's `0 | 1` as well as a boolean; absent means active.
 */
export interface EffectiveWindow {
  effective_from: string;
  effective_to?: string | null;
  is_active?: number | boolean | null;
}

/**
 * What a single version's dates say about it, today.
 *
 * Five of the six are mutually exclusive readings of the same two dates; the
 * sixth (`inactive`) short-circuits them, because the coverage rule ignores
 * inactive rows entirely and so must this.
 *
 *   'inactive'  `is_active` is falsy. The rule (coverage.ts:177) never
 *               considers this row, so neither do we. The versions table
 *               already renders its own "Inactive" badge — saying anything
 *               about the dates of a row that cannot be selected is noise.
 *   'expired'   started, and its end has passed. THE state this work exists
 *               for.
 *   'expiring'  in force, but ends within EXPIRING_SOON_DAYS.
 *   'current'   in force, with a bounded end further out than that.
 *   'open'      in force and `effective_to IS NULL` — open-ended, never
 *               expires. Distinct from 'current' so "never expires" is
 *               asserted against a literal instead of falling out of an
 *               absence, but rendered identically: silent.
 *   'scheduled' `effective_from` is in the future. Not yet in force.
 */
export type VersionEffectivity =
  | 'inactive'
  | 'expired'
  | 'expiring'
  | 'current'
  | 'open'
  | 'scheduled';

/**
 * The `YYYY-MM-DD` prefix of a date-ish string, so a date-only bound and an
 * ISO datetime compare like-for-like.
 *
 * CHARACTER-FOR-CHARACTER the function at coverage.ts:168. Kept in sync by
 * hand because the SPA cannot import Worker source. Production stores plain
 * dates on both sides, so this never fires today; it exists so that if one
 * side ever gains a time part, `'2026-07-01' >= '2026-07-01T00:00:00'` —
 * false lexicographically — cannot silently turn an in-force version into an
 * expired one.
 */
export function toDateKey(value: string): string {
  const t = value.indexOf('T');
  if (t === 10) return value.slice(0, 10);
  const sp = value.indexOf(' ');
  if (sp === 10) return value.slice(0, 10);
  return value;
}

/**
 * The exclusive end instant of an inclusive last day, as a comparable key.
 * `'2026-06-30'` → `'2026-07-01'`, so it can be compared like-for-like with
 * a bare "today" key.
 *
 * CHARACTER-FOR-CHARACTER coverage.ts:185. Kept in sync by hand because the
 * SPA cannot import Worker source. Uses UTC explicitly, matching that
 * file's own note: a local-time rollover would shift the boundary by a
 * day, which is exactly the class of error this function exists to
 * prevent — and exactly the class of error this file shipped with when it
 * used a raw comparison instead (see the module header).
 */
export function nextDayKey(inclusiveLastDay: string): string {
  const key = toDateKey(inclusiveLastDay);
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Today, as a `YYYY-MM-DD` key, from the viewer's LOCAL calendar.
 *
 * Local and not UTC on purpose. `effective_to` is a bare calendar date with no
 * zone; "has it passed?" is a question about the operator's wall calendar, and
 * the operator is in SAST (UTC+2). Deriving from `toISOString()` would answer
 * for UTC, which disagrees with the operator between 00:00 and 02:00 SAST —
 * a badge two hours stale, once a day. The repo's "pin TZ=UTC" rule is about
 * the telemetry/timestamp pipeline, where an hour offset moves data into the
 * wrong interval; nothing here is a timestamp.
 *
 * `now` is injectable so this is testable without the real clock. Constructed
 * from local components, so the result is the same in any runner timezone.
 */
export function todayDateKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Whole days from `fromKey` to `toKey`, or `null` if either is not a
 * `YYYY-MM-DD` key.
 *
 * Built on `Date.UTC` of the parsed parts rather than `new Date(string)`, so
 * there is no parsing ambiguity and no timezone in play at all — both operands
 * are the same fictional-UTC instant, and the difference between them is exact
 * whole days regardless of where the browser is or whether a DST boundary sits
 * between them.
 *
 * DISPLAY ONLY. See the module header: no state boundary is decided here.
 */
export function daysBetween(fromKey: string, toKey: string): number | null {
  const from = utcMillis(fromKey);
  const to = utcMillis(toKey);
  if (from === null || to === null) return null;
  return Math.round((to - from) / 86_400_000);
}

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function utcMillis(key: string): number | null {
  const m = DATE_KEY.exec(key);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Is this row active? Absent/null means active — mirrors coverage.ts:177. */
function isActive(version: EffectiveWindow): boolean {
  const flag = version.is_active;
  if (flag === undefined || flag === null) return true;
  return flag !== 0 && flag !== false;
}

/**
 * How many days this version has LEFT to run, counting today — `null` when
 * open-ended or the dates are unparseable. Does not itself check whether the
 * version is currently in force; both callers (`versionEffectivityLabel` and
 * `summariseTariff`, via `classifyVersion`) only reach this after already
 * establishing that.
 *
 * `1` means TODAY is its last day (`todayKey === effective_to`): computed as
 * the days from today up to `nextDayKey(effective_to)` — the same half-open
 * span `coverage.ts`'s `spanDays` would give for `{start: today, end:
 * nextDayKey(effective_to)}` — NOT `daysBetween(today, effective_to)`
 * directly, which undercounts by exactly one (reads `0` on the last day) and
 * is the bug this file previously shipped with; see the module header.
 */
export function daysUntilExpiry(version: EffectiveWindow, todayKey: string): number | null {
  const to = version.effective_to;
  if (to === null || to === undefined) return null;
  return daysBetween(toDateKey(todayKey), nextDayKey(to));
}

/**
 * Classify one version's dates against a day.
 *
 * `todayKey` is passed in, never read from the clock, so every caller — and
 * every test — is explicit about the day it is asking about.
 *
 * Ordering note: `expired` is tested BEFORE `scheduled`. They are disjoint for
 * any sane row, and overlap only for an inverted one (`effective_from` after
 * `effective_to`), which is in force never. Reading that row as `expired`
 * shows an operator a red badge on a broken window, which is the outcome that
 * gets it looked at; reading it as `scheduled` would imply it is merely
 * waiting its turn.
 */
export function classifyVersion(
  version: EffectiveWindow,
  todayKey: string,
  soonDays: number = EXPIRING_SOON_DAYS,
): VersionEffectivity {
  if (!isActive(version)) return 'inactive';

  const today = toDateKey(todayKey);
  const to = version.effective_to;

  // effective_to is the INCLUSIVE last day; nextDayKey converts it to the
  // EXCLUSIVE end before comparing — the same conversion coverage.ts
  // performs (coverage.ts:249-252), applied to today instead of a period
  // start. A version ending 2026-06-30 is expired starting 2026-07-01
  // (`nextDayKey('2026-06-30')`), not on 2026-06-30 itself.
  if (to !== null && to !== undefined && today >= nextDayKey(to)) return 'expired';
  if (toDateKey(version.effective_from) > today) return 'scheduled';
  if (to === null || to === undefined) return 'open';

  // In force with a bounded end. Only NOW does daysBetween enter, and only
  // to split 'current' from 'expiring' — both of which are in force.
  const days = daysBetween(today, nextDayKey(to));
  if (days !== null && days <= soonDays) return 'expiring';
  return 'current';
}

/** Do this version's dates put it in force on `todayKey`? */
export function isInForce(version: EffectiveWindow, todayKey: string, soonDays?: number): boolean {
  const state = classifyVersion(version, todayKey, soonDays);
  return state === 'current' || state === 'open' || state === 'expiring';
}

/**
 * What a whole TARIFF's versions say about it, today.
 *
 * This is the actionable unit. An operator does not fix a version, they fix a
 * tariff — and there are 21 broken ones to find.
 *
 *   'ok'        some version is in force with room to spare. Silent.
 *   'expiring'  every version in force ends within the threshold.
 *   'expired'   nothing is in force, but something once was. THE 21.
 *   'scheduled' nothing is in force and nothing ever was, but a version
 *               starts later. A real gap today, with a different remedy
 *               (wait / correct the start date) from 'expired' (add a
 *               version).
 *   'none'      no active version at all — the tariff has none, or every one
 *               is `is_active = 0`. Silent: the card already prints its own
 *               version count, and this is not an expiry story.
 */
export type TariffEffectivity = 'ok' | 'expiring' | 'expired' | 'scheduled' | 'none';

export interface TariffEffectivitySummary {
  state: TariffEffectivity;
  /**
   * Days until the tariff stops being billable. Set only for `'expiring'`;
   * `null` for every other state.
   */
  daysUntilExpiry: number | null;
}

/**
 * Roll a tariff's versions up into one answer.
 *
 * `versions` is every row for the one tariff, in any order — no pre-sorting or
 * pre-filtering, same contract as `resolveTariffCoverage` (coverage.ts:231).
 *
 * When two versions are in force at once (overlapping windows — a data
 * oddity, but the model permits it) the tariff stops being billable when the
 * LAST of them ends, so `daysUntilExpiry` is the maximum, not the minimum.
 */
export function summariseTariff(
  versions: readonly EffectiveWindow[],
  todayKey: string,
  soonDays: number = EXPIRING_SOON_DAYS,
): TariffEffectivitySummary {
  let sawExpired = false;
  let sawScheduled = false;
  let sawUnbounded = false;
  let latestExpiry: number | null = null;

  for (const version of versions) {
    switch (classifyVersion(version, todayKey, soonDays)) {
      case 'inactive':
        break;
      case 'expired':
        sawExpired = true;
        break;
      case 'scheduled':
        sawScheduled = true;
        break;
      case 'current':
      case 'open':
        sawUnbounded = true;
        break;
      case 'expiring': {
        const days = daysUntilExpiry(version, todayKey);
        if (days !== null && (latestExpiry === null || days > latestExpiry)) latestExpiry = days;
        break;
      }
    }
  }

  if (sawUnbounded) return { state: 'ok', daysUntilExpiry: null };
  if (latestExpiry !== null) return { state: 'expiring', daysUntilExpiry: latestExpiry };
  if (sawExpired) return { state: 'expired', daysUntilExpiry: null };
  if (sawScheduled) return { state: 'scheduled', daysUntilExpiry: null };
  return { state: 'none', daysUntilExpiry: null };
}

// ---------------------------------------------------------------------------
// Copy
//
// Here rather than inline in the page for the same reason `tariffCoverage.ts`
// keeps its wording pure: the words ARE the deliverable, and they should be
// assertable without rendering anything.
// ---------------------------------------------------------------------------

function days(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/**
 * The badge label for one version row, or `null` when the state is silent.
 *
 * `current`, `open` and `inactive` are silent BY DESIGN. A version that is
 * simply fine must not gain a badge — the common case gaining noise is how a
 * warning stops being read (design spec §5.2).
 */
export function versionEffectivityLabel(
  version: EffectiveWindow,
  todayKey: string,
  soonDays: number = EXPIRING_SOON_DAYS,
): string | null {
  switch (classifyVersion(version, todayKey, soonDays)) {
    case 'expired':
      return 'Expired';
    case 'expiring': {
      const n = daysUntilExpiry(version, todayKey);
      return n === null ? 'Expiring' : `Expires in ${days(n)}`;
    }
    case 'scheduled':
      return 'Not yet in effect';
    default:
      return null;
  }
}

/** The badge label for a whole tariff, or `null` when the state is silent. */
export function tariffEffectivityLabel(summary: TariffEffectivitySummary): string | null {
  switch (summary.state) {
    case 'expired':
      return 'No version in effect';
    case 'expiring':
      return summary.daysUntilExpiry === null
        ? 'Expiring'
        : `Expires in ${days(summary.daysUntilExpiry)}`;
    case 'scheduled':
      return 'Not yet in effect';
    default:
      return null;
  }
}
